import type { GoalSnapshot } from '@/api/attestations';
import type { ChiefComplaint } from '@/api/dashboard';

/**
 * Shapes the chief complaint list into the rows the goals commitment modal
 * shows and the snapshot it records (PLA-1110). Pure, so it is unit-testable
 * without React.
 */

export type GoalStatus = 'met' | 'below' | 'no_data';

export interface GoalRow extends GoalSnapshot {
  status: GoalStatus;
  /** True when the goal is the app-wide fallback rather than an org-set target. */
  isDefault: boolean;
}

const round = (n: number) => Math.round(n);

export function toGoalRows(complaints: ChiefComplaint[]): GoalRow[] {
  const rows: GoalRow[] = [];
  for (const c of complaints) {
    if (c.goal == null || !Number.isFinite(c.goal)) continue;
    const goal = round(c.goal);
    const score = c.score == null || !Number.isFinite(c.score) ? null : round(c.score);
    rows.push({
      id: c.id,
      name: c.name,
      goal,
      score,
      status: score == null ? 'no_data' : score >= goal ? 'met' : 'below',
      isDefault: c.goalSource === 'default',
    });
  }
  // Furthest below goal first, so the work sits at the top; met goals next;
  // rows with no data last. Ties by name for a stable list.
  const rank: Record<GoalStatus, number> = { below: 0, met: 1, no_data: 2 };
  return rows.sort((a, b) => {
    if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
    if (a.status === 'below' && b.status === 'below') {
      return b.goal - (b.score ?? 0) - (a.goal - (a.score ?? 0));
    }
    return a.name.localeCompare(b.name);
  });
}

/** What the API stores: the rows without presentation fields. */
export const toGoalSnapshots = (rows: GoalRow[]): GoalSnapshot[] =>
  rows.map(({ id, name, goal, score }) => ({ id, name, goal, score }));
